import React, { useEffect, useState } from "react";
import { FileText, AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import ShowReasoning from "@/components/ShowReasoning";

export default function ContractIntelligence() {
  const [list, setList] = useState([]);
  const [renew, setRenew] = useState([]);
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/contracts/list").then(r=>setList(r.data));
    api.get("/contracts/renewals").then(r=>setRenew(r.data));
  }, []);

  const analyze = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const data = await aiJob("/contracts/analyze", { contract_text: text, specialty: "Prosthodontics", region: "California" });
      setResult({ text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer });
      toast.success("Contract analyzed");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const cols = [
    { key: "payer", label: "Payer" },
    { key: "type", label: "Type" },
    { key: "renewal", label: "Renewal" },
    { key: "revenue_impact", label: "Revenue Impact", render: (r)=>`$${r.revenue_impact.toLocaleString()}` },
  ];

  const totalImpact = list.reduce((s,c)=>s+c.revenue_impact, 0);

  return (
    <>
      <PageHeader eyebrow="Module 26" title="Contract Intelligence"
        subtitle="Payer contract upload + AI extraction, benchmark comparison, renegotiation letter drafting, revenue impact simulation." testid="contract-page" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiCard icon={FileText} label="Active Contracts" value={list.length} testid="kpi-active" />
        <KpiCard label="Annualized Impact" value={`$${(totalImpact/1000).toFixed(0)}k`} testid="kpi-impact" />
        <KpiCard icon={AlertTriangle} label="Renewals ≤ 180d" value={renew.filter(r=>r.alert).length} accent testid="kpi-renewals" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Contracts" testid="contracts-section">
          <DataTable data={list} columns={cols} searchKeys={["payer","type"]} testid="contracts-table" />
        </SectionCard>
        <SectionCard title="Upcoming Renewals" testid="renewals-section">
          <div className="space-y-2">
            {renew.map((r,i)=>(
              <div key={i} className={`p-3 border rounded-md flex justify-between items-center ${r.alert?"border-red-200 bg-red-50":"border-brand bg-white"}`}>
                <div>
                  <div className="text-sm font-medium">{r.payer}</div>
                  <div className="text-xs text-brand-muted">Renews {new Date(r.renewal).toLocaleDateString()}</div>
                </div>
                <span className={`text-lg font-semibold ${r.alert?"text-red-600":"text-brand-text"}`}>{r.days_remaining}<span className="text-xs ml-1 text-brand-muted">d</span></span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="AI Contract Analyzer" subtitle="Paste contract text — Topology extracts every rate, clause, and renegotiation argument." testid="contract-analyzer">
        <textarea value={text} onChange={(e)=>setText(e.target.value)} rows={6} placeholder="Paste payer contract terms…"
          className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="contract-text" />
        <button onClick={analyze} disabled={busy} className="mt-2 bg-brand-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center gap-2 disabled:opacity-50" data-testid="contract-analyze-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Analyze
        </button>
        {result && (
          <div className="mt-3">
            <AIOutputDisclaimer />
            <pre className="text-xs whitespace-pre-wrap bg-brand-bg p-3 rounded-md max-h-72 overflow-y-auto" data-testid="contract-result">{result.text}</pre>
            {result.verification_footer && (
              <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="contract-verification-footer">{result.verification_footer}</div>
            )}
            <ShowReasoning reasoning={result.reasoning} score={result.score} testid="contract-reasoning" />
            <AIFeedback module="contract-intelligence" outputType="contract_analysis" testid="contract-feedback" />
          </div>
        )}
      </SectionCard>
    </>
  );
}
